/* ICExtract Ajax cart drawer — vanilla, no dependencies.
   Cart Ajax API (/cart/add.js, /cart/change.js, /cart.js) + Section Rendering API
   (?sections=ic-cart-drawer) for re-rendering the drawer contents.
   Opens on the `ic:cart:added` event dispatched by ic-product.js / ic-sticky-atc.js,
   and handles its own quick-add, quantity, remove, and note interactions. */
(function () {
  'use strict';

  // Run-once guard: theme.liquid renders this section once, but be defensive
  // in case the section is duplicated by a preview/editor injection.
  if (window.__icCartDrawerInit) return;
  window.__icCartDrawerInit = true;

  var SECTION_ID = 'ic-cart-drawer';
  var strings = window.icCartStrings || {};
  var drawerEnabled = window.icCartDrawerEnabled !== false;

  var drawer = document.querySelector('[data-ic-cart-drawer]');
  var backdrop = document.querySelector('[data-ic-cart-backdrop]');
  if (!drawer || !backdrop) return;

  var form = drawer.querySelector('[data-ic-cart-form]');
  var status = drawer.querySelector('[data-ic-cart-status]');
  var closeBtn = drawer.querySelector('[data-ic-cart-close]');
  var lastFocused = null;
  var busy = false;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function money(cents) {
    if (window.Shopify && window.Shopify.formatMoney && window.icMoneyFormat) {
      return window.Shopify.formatMoney(cents, window.icMoneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function announce(msg) {
    if (status) status.textContent = msg || '';
  }

  /* ================= Focus / open / close ================= */

  function focusables() {
    return Array.prototype.filter.call(
      drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      ),
      function (el) { return el.offsetParent !== null; }
    );
  }

  function openDrawer() {
    if (drawer.classList.contains('is-open')) return;
    lastFocused = document.activeElement;
    drawer.hidden = false;
    backdrop.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    void drawer.offsetWidth; // reflow so the transition runs
    drawer.classList.add('is-open');
    backdrop.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeydown);
    var els = focusables();
    if (closeBtn) closeBtn.focus();
    else if (els.length) els[0].focus();
  }

  function closeDrawer() {
    if (!drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    backdrop.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKeydown);
    window.setTimeout(function () {
      if (!drawer.classList.contains('is-open')) {
        drawer.hidden = true;
        backdrop.hidden = true;
      }
    }, 320);
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  function onKeydown(e) {
    if (e.key === 'Escape') { closeDrawer(); return; }
    if (e.key !== 'Tab') return;
    var els = focusables();
    if (!els.length) return;
    var first = els[0];
    var last = els[els.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
  backdrop.addEventListener('click', closeDrawer);

  // Expose a programmatic API for other scripts.
  window.icCartDrawer = { open: openDrawer, close: closeDrawer, refresh: refresh };

  /* ================= Cart count bubbles ================= */

  function updateCounts(count) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-ic-cart-count]'), function (el) {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
    });
    Array.prototype.forEach.call(document.querySelectorAll('.ic-header__action--cart'), function (el) {
      var label = (window.icStrings && window.icStrings.cartCount) || null;
      el.setAttribute('aria-label', label ? label.replace('{{ count }}', count) : (count + ' items in cart'));
    });
  }

  /* ================= Section Rendering + state refresh ================= */

  function refresh() {
    var url = root() + '?sections=' + encodeURIComponent(SECTION_ID);
    return fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data && data[SECTION_ID];
        if (!html) return refreshFromCart();
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var newForm = parsed.querySelector('[data-ic-cart-form]');
        var currentForm = drawer.querySelector('[data-ic-cart-form]');
        if (newForm && currentForm) currentForm.innerHTML = newForm.innerHTML;
        syncCountFromContents();
      })
      .catch(function () { return refreshFromCart(); });
  }

  // Fallback: rebuild count/subtotal from cart.js if section rendering fails.
  function refreshFromCart() {
    return fetch(root() + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        updateCounts(cart.item_count);
        var subtotal = drawer.querySelector('[data-ic-cart-subtotal]');
        if (subtotal) subtotal.textContent = money(cart.total_price);
      })
      .catch(function () {});
  }

  function syncCountFromContents() {
    var contents = drawer.querySelector('[data-ic-cart-contents]');
    if (!contents) return;
    var count = parseInt(contents.getAttribute('data-cart-count'), 10) || 0;
    updateCounts(count);
  }

  /* ================= Add to cart (quick-add + fallback) ================= */

  function addById(id, quantity, btn, errEl) {
    if (busy) return Promise.resolve();
    busy = true;
    if (btn) { btn.classList.add('is-loading'); btn.disabled = true; }
    if (errEl) { errEl.textContent = ''; errEl.classList.remove('is-visible'); }
    return fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: quantity || 1 }] })
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        busy = false;
        if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; }
        if (!res.ok) {
          var msg = (res.data && res.data.description) || strings.addError || 'Could not add to cart';
          if (errEl) { errEl.textContent = msg; errEl.classList.add('is-visible'); }
          announce(msg);
          return;
        }
        document.dispatchEvent(new CustomEvent('ic:cart:added', { detail: { item: res.data } }));
      })
      .catch(function () {
        busy = false;
        if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; }
        var msg = strings.addError || 'Could not add to cart';
        if (errEl) { errEl.textContent = msg; errEl.classList.add('is-visible'); }
        announce(msg);
      });
  }

  /* Quick-add on product cards. A shared guard attribute prevents double-binding
     with ic-collection.js (which handles the same buttons on collection pages). */
  function bindQuickAdd(scope) {
    var btns = (scope || document).querySelectorAll('[data-ic-quick-add-btn]');
    Array.prototype.forEach.call(btns, function (btn) {
      if (btn.getAttribute('data-ic-quick-add-bound')) return;
      btn.setAttribute('data-ic-quick-add-bound', '');
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-variant-id');
        if (!id) return;
        var errEl = btn.parentNode ? btn.parentNode.querySelector('[data-ic-quick-add-error]') : null;
        addById(id, 1, btn, errEl);
      });
    });
  }
  bindQuickAdd(document);

  /* ================= Change quantity / remove ================= */

  function changeLine(line, quantity, errEl) {
    if (busy) return;
    busy = true;
    drawer.classList.add('is-updating');
    fetch(root() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Number(quantity) })
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        busy = false;
        drawer.classList.remove('is-updating');
        if (!res.ok) {
          var msg = (res.data && res.data.description) || strings.addError || 'Could not update cart';
          if (errEl) { errEl.textContent = msg; errEl.classList.add('is-visible'); }
          announce(msg);
          return;
        }
        refresh();
        announce(strings.viewCart || '');
      })
      .catch(function () {
        busy = false;
        drawer.classList.remove('is-updating');
      });
  }

  /* Delegated handlers within the drawer form. */
  if (form) {
    form.addEventListener('click', function (e) {
      var remove = e.target.closest('[data-ic-cart-remove]');
      if (remove) {
        e.preventDefault();
        changeLine(remove.getAttribute('data-line'), 0);
        return;
      }
      var down = e.target.closest('[data-ic-cart-qty-down]');
      var up = e.target.closest('[data-ic-cart-qty-up]');
      if (down || up) {
        var btn = down || up;
        var line = btn.getAttribute('data-line');
        var input = form.querySelector('[data-ic-cart-qty-input][data-line="' + line + '"]');
        if (!input) return;
        var v = parseInt(input.value, 10) || 0;
        v = down ? Math.max(0, v - 1) : v + 1;
        var errEl = btn.closest('[data-ic-cart-item]');
        errEl = errEl ? errEl.querySelector('[data-ic-cart-item-error]') : null;
        changeLine(line, v, errEl);
      }
    });

    form.addEventListener('change', function (e) {
      var input = e.target.closest('[data-ic-cart-qty-input]');
      if (!input) return;
      var line = input.getAttribute('data-line');
      var v = parseInt(input.value, 10);
      if (isNaN(v) || v < 0) v = 0;
      var itemEl = input.closest('[data-ic-cart-item]');
      var errEl = itemEl ? itemEl.querySelector('[data-ic-cart-item-error]') : null;
      changeLine(line, v, errEl);
    });

    // Persist order note (debounced) via /cart/update.js.
    var noteTimer = null;
    form.addEventListener('input', function (e) {
      var note = e.target.closest('[data-ic-cart-note]');
      if (!note) return;
      window.clearTimeout(noteTimer);
      noteTimer = window.setTimeout(function () {
        fetch(root() + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ note: note.value })
        }).catch(function () {});
      }, 500);
    });

    // The checkout button submits the form to routes.cart_url with name="checkout".
    // Let the browser handle that natively (no preventDefault).
  }

  /* ================= React to add events from other scripts ================= */

  document.addEventListener('ic:cart:added', function () {
    refresh().then(function () {
      if (drawerEnabled) {
        openDrawer();
        announce(strings.added || 'Item added to your cart');
      }
    });
  });

  // Re-bind quick-add for any DOM injected after load (e.g. related products,
  // filtered collection grids swapped in by ic-collection.js).
  if ('MutationObserver' in window) {
    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
          bindQuickAdd(document);
          break;
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  // Initial header count sync from server-rendered contents.
  syncCountFromContents();
})();
